import React, { useEffect, useMemo } from 'react';
import { HistoryTable } from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { HistoryRow, HistoryStatus } from './driverHistoryTypes';
import { useDeliveriesStore } from '../../../store/deliveriesStore';
import { formatRouteId } from '../../../utils/formatId';
import styles from './DriverHistory.module.css';

const DriverHistory: React.FC = () => {
  const stops = useDeliveriesStore((s) => s.stops);
  const loading = useDeliveriesStore((s) => s.loading);
  const error = useDeliveriesStore((s) => s.error);
  const fetchMyStops = useDeliveriesStore((s) => s.fetchMyStops);

  useEffect(() => { fetchMyStops(); }, [fetchMyStops]);

  // Group completed/failed stops by route. Currently routes themselves don't
  // have a per-driver history endpoint, so we aggregate from the stops view.
  const rows = useMemo<HistoryRow[]>(() => {
    const byRoute = new Map<number, { stops: number; orders: number; status: HistoryStatus; date: string }>();
    for (const s of stops) {
      if (s.status !== 'delivered' && s.status !== 'failed') continue;
      const entry = byRoute.get(s.delivery_route_id) ?? { stops: 0, orders: 0, status: 'Completed', date: s.completed_at || s.attempted_at || s.created_at };
      entry.stops += 1;
      entry.orders += 1;
      if (s.status === 'failed') entry.status = entry.status === 'Completed' ? 'Partial' : entry.status;
      if (s.completed_at && new Date(s.completed_at).getTime() > new Date(entry.date).getTime()) entry.date = s.completed_at;
      byRoute.set(s.delivery_route_id, entry);
    }
    return Array.from(byRoute.entries())
      .sort((a, b) => new Date(b[1].date).getTime() - new Date(a[1].date).getTime())
      .map(([routeId, e]) => ({
        id: String(routeId),
        routeId: formatRouteId(routeId),
        date: new Date(e.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        numberOfStops: `${e.stops} stops`,
        totalOrders: `${e.orders} orders`,
        status: e.status,
      }));
  }, [stops]);

  return (
    <div className={styles.container}>
      <AsyncBoundary
        loading={loading && stops.length === 0}
        error={error}
        empty={!loading && rows.length === 0}
        emptyMessage="No delivery history yet"
        onRetry={() => fetchMyStops()}
      >
        <HistoryTable rows={rows} />
      </AsyncBoundary>
    </div>
  );
};

export const DriverHistoryPage: React.FC = () => <DriverHistory />;

export { DriverHistory };
export default DriverHistory;
